import { useQuery } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"
import { getLoan } from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatCurrency, formatDate, formatDateTime } from "../utils/formatting"

export function LoanDetailPage() {
  const navigate = useNavigate()
  const { loanId } = useParams()
  const loanQuery = useQuery({
    queryKey: ["portal-loan", loanId],
    queryFn: () => getLoan(loanId!).then((response) => response.data),
    enabled: Boolean(loanId),
  })

  if (loanQuery.isLoading) {
    return (
      <div className="page-stack">
        <Skeleton height={120} />
        <Skeleton height={220} />
        <Skeleton height={220} />
      </div>
    )
  }

  const loan = loanQuery.data
  if (!loan) return null

  return (
    <div className="page-stack">
      <section className="card">
        <div className="loan-card-header">
          <div>
            <p className="mono">{loan.loanId}</p>
            <h2>{loan.productName}</h2>
          </div>
          <span className="status-pill neutral">{loan.overdueLabel.replaceAll("_", " ")}</span>
        </div>
        {loan.loanType === "MICRO" ? (
          <div className="status-banner success" style={{ marginTop: 16 }}>
            Net received: {formatCurrency(loan.currency, loan.netDisbursedAmount ?? 0)}. Quick loans are repaid as a
            single bullet payment by {formatDate(loan.dueDate)}.
          </div>
        ) : null}
        <dl className="metric-grid">
          <div>
            <dt>Principal</dt>
            <dd>{formatCurrency(loan.currency, loan.principalAmount)}</dd>
          </div>
          <div>
            <dt>Total repayable</dt>
            <dd>{formatCurrency(loan.currency, loan.totalRepayableAmount)}</dd>
          </div>
          <div>
            <dt>Outstanding</dt>
            <dd>{formatCurrency(loan.currency, loan.outstandingBalance)}</dd>
          </div>
          <div>
            <dt>Interest rate</dt>
            <dd>{(loan.interestRate * 100).toFixed(0)}%</dd>
          </div>
          {loan.loanType === "MICRO" ? (
            <div>
              <dt>Application fee</dt>
              <dd>{formatCurrency(loan.currency, loan.applicationFeeAmount)}</dd>
            </div>
          ) : null}
          {loan.loanType === "MICRO" ? (
            <div>
              <dt>Net received</dt>
              <dd>{formatCurrency(loan.currency, loan.netDisbursedAmount ?? 0)}</dd>
            </div>
          ) : null}
          {loan.loanType === "MICRO" ? (
            <div>
              <dt>Tier</dt>
              <dd>{loan.microLoanTier ?? "-"}</dd>
            </div>
          ) : null}
          <div>
            <dt>Disbursed</dt>
            <dd>{formatDate(loan.disbursementDate)}</dd>
          </div>
          <div>
            <dt>Due date</dt>
            <dd>{formatDate(loan.dueDate)}</dd>
          </div>
        </dl>
        <div className="button-row">
          <button className="primary-button" onClick={() => navigate(`/payments?loanId=${loan.loanId}`)} type="button">
            Pay now
          </button>
          <button
            className="ghost-button"
            onClick={() => navigate(`/support?loanId=${loan.loanId}&ticketType=DISPUTE`)}
            type="button"
          >
            Raise dispute
          </button>
        </div>
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Repayment schedule</p>
            <h3>{loan.loanType === "MICRO" ? "Bullet repayment" : "Expected payment"}</h3>
          </div>
        </div>
        {loan.loanType === "MICRO" ? (
          <p className="subtle" style={{ marginBottom: 16 }}>
            The full total repayable amount is due on the due date shown below.
          </p>
        ) : null}
        {loan.repaymentSchedule.map((entry: any) => (
          <div className="list-row" key={entry.paymentNumber}>
            <div>
              <strong>Payment {entry.paymentNumber}</strong>
              <p className="subtle">Due {formatDate(entry.expectedDate)}</p>
            </div>
            <div className="align-right">
              <strong>{formatCurrency(loan.currency, entry.expectedAmount)}</strong>
              <p className="subtle">{entry.status}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Penalty breakdown</p>
            <h3>How your balance changed</h3>
          </div>
        </div>
        {loan.penaltyBreakdown.map((item: any, index: number) => (
          <div className="list-row" key={`${item.label}-${index}`}>
            <div>
              <strong>{item.label}</strong>
              <p className="subtle">{formatDate(item.appliedAt)}</p>
            </div>
            <div className="align-right">
              <strong>{formatCurrency(loan.currency, item.amount)}</strong>
              {item.waived ? <p className="subtle">Waived</p> : null}
            </div>
          </div>
        ))}
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Repayment history</p>
            <h3>Recorded payments</h3>
          </div>
        </div>
        {loan.repaymentHistory.length === 0 ? (
          <p className="empty-copy">No repayments recorded yet.</p>
        ) : (
          loan.repaymentHistory.map((payment: any) => (
            <div className="list-row" key={payment.paymentId}>
              <div>
                <strong>{formatCurrency(payment.currency, payment.amount)}</strong>
                <p className="subtle">
                  {payment.transactionReference ?? "No reference"} · {payment.status}
                </p>
              </div>
              <span className="subtle">{formatDateTime(payment.paymentDate)}</span>
            </div>
          ))
        )}
      </section>
    </div>
  )
}
